import React, { useRef, useEffect, useState, useCallback } from 'react';
import Globe from 'react-globe.gl';
import { apiClient } from '../../api_actions/index';
import { useIsSuperAdmin } from '../../store';
import LoadingOrbitalSpinner from '../../components/LoadingOrbitalSpinner/LoadingOrbitalSpinner';
import { icon } from '@fortawesome/fontawesome-svg-core'
import { faLaptop, faMobile, faVideo, faMicrochip, faMapPin } from '@fortawesome/free-solid-svg-icons'
import { faCameraCctv, faMicrochipAi } from '@fortawesome/pro-duotone-svg-icons';

const ARC_ANIMATION_DURATION = 1500;
const REFRESH_INTERVAL = 10000;
const OPACITY = 0.3;

export default function GlobePage({ isHalfScreen = false }) {
  const [loading, setLoading] = useState(true);
  const [arcs, setArcs] = useState([]);
  const [markers, setMarkers] = useState([]);  // stable state for globe markers
  const isSuperAdmin = useIsSuperAdmin();
  const isVMSActiveRef = useRef(false);
  const hoveredArcRef = useRef(null);
  const globeRef = useRef();
  const arcTimerRef = useRef();              // track arc animation timer across refreshes
  const containerRef = useRef();
  const processData = useCallback((data) => {
    // --- Build markers ---
    const deploymentMarkers = data
      .map((deployment) => {
        const lat = deployment.location?.latitude ?? deployment.router_info?.latitude;
        const lng = deployment.location?.longitude ?? deployment.router_info?.longitude;
        if (!lat || !lng) return null;
        return {
          lat,
          lng,
          label: deployment.hardware_info?.tag || deployment.location?.name || 'Unknown',
          name: deployment.location?.name,
          health: deployment.deployment_health?.name,
          isUser: false,
          isActive:  (deployment.current_vms_users || []).length > 0
        };
      })
      .filter(Boolean);

      const userMarkers = Object.values(
        data.reduce((acc, deployment) => {
          (deployment.current_vms_users || []).forEach((user) => {
            if (!user.ip_lat || !user.ip_long) return;
            if (!acc[user.user_id]) {
              acc[user.user_id] = {
                lat: user.ip_lat,
                lng: user.ip_long,
                label: `${user.first_name} ${user.last_name}`,
                email: user.email,
                isUser: true,
                isMobile:user.mobile,
                vmsMode:user.vms_mode
              };
            }
          });
          return acc;
        }, {})
      );

    setMarkers([...deploymentMarkers, ...userMarkers]);

    // --- Build arcs ---
    const animatingArcs = [];

    data.forEach((deployment) => {
      const deploymentLat = deployment.location?.latitude ?? deployment.router_info?.latitude;
      const deploymentLng = deployment.location?.longitude ?? deployment.router_info?.longitude;
      isVMSActiveRef.current = false
      if (!deploymentLat || !deploymentLng) return; 
      (deployment.current_vms_users || []).forEach((user) => {
        if (!user.ip_lat || !user.ip_long) return;
        animatingArcs.push({
          startLat: user.ip_lat,
          startLng: user.ip_long,
          endLat: deploymentLat,
          endLng: deploymentLng,
          vmsMode: user.vms_mode,  // carry it through
        });
      }
    )});

    // Clear any previous arc animation timer before starting a new one
    if (arcTimerRef.current) clearTimeout(arcTimerRef.current);
    isVMSActiveRef.current = animatingArcs.length > 0;
    setArcs(animatingArcs);

    arcTimerRef.current = setTimeout(() => {
      setArcs(animatingArcs.map(arc => ({ ...arc, animate: true })));
    }, ARC_ANIMATION_DURATION);

  }, []);

  const loadData = useCallback(async (isBackgroundRefresh = false) => {
    try {
      if (!isBackgroundRefresh) setLoading(true);
      const result = await apiClient.post("read/deployments?include_vms_info=true");
      processData(result?.data || []);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      if (!isBackgroundRefresh) setLoading(false);
    }
  }, [processData]);

  // Initial load + polling interval
  useEffect(() => {
    loadData(false);

    const interval = setInterval(() => {
      loadData(true);  // background — no spinner, no globe reset
    }, REFRESH_INTERVAL);

    return () => {
      clearInterval(interval);
      if (arcTimerRef.current) clearTimeout(arcTimerRef.current);
    };
  }, [loadData]);

  // Point of view — fires once after initial load
  useEffect(() => {
    if (!loading && globeRef.current) {
      globeRef.current.pointOfView(
        { lat: 25.761681, lng: -80.191788, altitude: 1.0 },
        1500
      );
    }
  }, [loading]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const stopPropagation = (e) => {
      e.stopPropagation();
      e.preventDefault(); // prevents page zoom on Ctrl+scroll
    };

    // Must be non-passive to allow preventDefault()
    container.addEventListener('wheel', stopPropagation, { passive: false });

    return () => {
      container.removeEventListener('wheel', stopPropagation);
    };
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', padding: '32px' }}>
        <LoadingOrbitalSpinner />
        <div>Loading map data...</div>
      </div>
    );
  }

  return (
    <div
      ref={containerRef} 
      className={`map-page-container ${isHalfScreen ? 'half-screen' : ''}`}
      style={{ width: '100%', height: '100%', borderLeft: '4px solid #e8e8e8', borderTop: '4px solid #e8e8e8', borderBottom: '4px solid #e8e8e8', borderRight:'4px solid #e8e8e8', overflow: 'hidden'}}
    >
      <Globe
        ref={globeRef}
        backgroundImageUrl="https://unpkg.com/three-globe/example/img/night-sky.png"
        globeTileEngineUrl={(x, y, l) => `https://tile.openstreetmap.org/${l}/${x}/${y}.png`}
        showAtmosphere={false}
        htmlElementsData={markers}
        htmlTransitionDuration={0}
        htmlElement={(d) => {
          const wrapper = document.createElement('div');
          wrapper.style.display = 'flex';
          wrapper.style.flexDirection = 'column';
          wrapper.style.alignItems = 'center';
          wrapper.style.gap = '4px';
          wrapper.style.color = 'white';
          wrapper.style.transform = 'translate(-50%, -100%)'; // apply to all markers

          if (d.isUser) {
            if (d.isMobile) {
              const iconWrapper = document.createElement('div');
              iconWrapper.innerHTML = icon(faMobile).html[0]; // FA-controlled, lower risk

              const span = document.createElement('span');
              span.style.whiteSpace = 'nowrap';
              span.style.textShadow = '0 0 3px black';
              span.textContent = d.name || d.label; // textContent never parses HTML

              wrapper.appendChild(iconWrapper.firstChild);
              wrapper.appendChild(span);
            } else {
              const iconWrapper = document.createElement('div');
              iconWrapper.innerHTML = icon(faLaptop).html[0]; // FA-controlled, lower risk

              const span = document.createElement('span');
              span.style.whiteSpace = 'nowrap';
              span.style.textShadow = '0 0 3px black';
              span.textContent = d.name || d.label; // textContent never parses HTML

              wrapper.appendChild(iconWrapper.firstChild);
              wrapper.appendChild(span);

            }
          } else {
            const videoWrapper = document.createElement('span');
            videoWrapper.innerHTML = icon(faCameraCctv).html[0];

            const ChipWrapper = document.createElement('span');
            ChipWrapper.innerHTML = icon(faMicrochipAi).html[0];

            const label = document.createElement('span');
            label.style.cssText = 'white-space: nowrap; color black;';
            label.textContent = d.name || d.label;

            wrapper.appendChild(ChipWrapper);
            wrapper.appendChild(videoWrapper);
            wrapper.appendChild(label);

            wrapper.addEventListener('mouseenter', () => {
              wrapper.style.opacity = '1.0';
            });

            wrapper.addEventListener('mouseleave', () => {
              if (!d.isActive && isVMSActiveRef.current) {
                wrapper.style.opacity = '0.15';
              }
            });

            if (!d.isActive && isVMSActiveRef.current) {
              wrapper.style.opacity = '0.25';
            }
          }
          wrapper.style.color = '#002B53';
          return wrapper;
        }}

        arcsData={arcs}
        arcColor="color"
        arcAltitude={0.2}
        arcDashLength={(arc) => arc.animate ? 0.4 : 1}
        arcDashGap={(arc) => arc.animate ? 0.1 : 0}
        arcDashAnimateTime={(d) => d.vmsMode === 'playback' ? 800 : ARC_ANIMATION_DURATION}
        arcStroke={(d) => ['archive', 'timelapse'].includes(d.vmsMode) ? 1 : 0.5}
        arcDashAnimateTime={(d) => ['archive', 'timelapse'].includes(d.vmsMode) ? 3000 : ARC_ANIMATION_DURATION}
        arcColor={(d) => {
          const op = !hoveredArcRef.current ? OPACITY : d === hoveredArcRef.current ? 0.9 : OPACITY / 4;

          // color by vms_mode
          if (d.vmsMode === 'live') return `rgba(0, 255, 0, ${op})`;       // green
          if (d.vmsMode === 'playback') return `rgba(255, 165, 0, ${op})`; // orange
          if (d.vmsMode === 'archive') return `rgba(0, 150, 255, ${op})`;
          if (d.vmsMode === 'timelapse') return `rgba(155, 0, 255, ${op})`;

          return `rgba(0, 0, 0, ${op})`; // fallback
        }}
        onArcHover={(arc) => {
          hoveredArcRef.current = arc;
          globeRef.current.arcColor(d => {
            const op = !arc ? OPACITY : d === arc ? 0.9 : OPACITY / 4;

            if (d.vmsMode === 'live') return `rgba(0, 255, 0, ${op})`;
            if (d.vmsMode === 'playback') return `rgba(255, 165, 0, ${op})`;
            if (d.vmsMode === 'archive') return `rgba(0, 150, 255, ${op})`;
            if (d.vmsMode === 'timelapse') return `rgba(155, 0, 255, ${op})`;

            return `rgba(0, 0, 0, ${op})`;
          });
        }}
      />
    </div>
  );
}